/**
 * Cart Error Handler Utility
 *
 * Centralized error parsing for cart operations to ensure consistent,
 * user-friendly error messages across the application.
 *
 * Prevents duplicate toast notifications by returning whether to show a toast.
 */

interface ErrorResponse {
  error?: string;
  token_expired?: boolean;
  new_token?: string;
  store?: string;
}

export interface ParsedCartError {
  message: string;
  shouldShowToast: boolean;
}

/**
 * Parse cart API error responses and return user-friendly error messages
 *
 * Handles:
 * - 8g concentrate limit errors
 * - Stock availability errors with specific quantities
 * - Generic fallback errors
 *
 * @param responseData - The response data from the cart API
 * @param response - Optional Response object
 * @returns Parsed error with message and whether to show a toast
 */
export function parseCartError(responseData: ErrorResponse, response?: Response): ParsedCartError {
  // No error in response
  if (!responseData?.error) {
    return {
      message: '',
      shouldShowToast: false,
    };
  }

  let errorMessage = 'Failed to update cart. Please try again.';

  // Parse GraphQL error structure
  try {
    const parsedError = JSON.parse(responseData.error.replace('GraphQL update error: ', ''));
    if (Array.isArray(parsedError) && parsedError.length > 0 && parsedError[0]?.message) {
      errorMessage = parsedError[0].message;
    }
  } catch (parseError) {
    errorMessage = responseData.error;
  }

  // Handle 8g concentrate limit
  if (
    errorMessage.toUpperCase().includes('PURCHASE_LIMIT_EXCEEDED') ||
    errorMessage.includes('8g') ||
    errorMessage.toLowerCase().includes('concentrate limit') ||
    errorMessage.toLowerCase().includes('grams of concentrated product')
  ) {
    return {
      message: '8g concentrate limit reached. Please adjust your cart.',
      shouldShowToast: true,
    };
  }

  // Handle stock availability errors
  // Error format: "requested quantity, 4, unavailable. Only 2 are available"
  if (errorMessage.includes('requested quantity') && errorMessage.includes('unavailable')) {
    const stockMatch = errorMessage.match(/Only (\d+) (?:are|is) available/i);

    if (stockMatch) {
      const availableStock = parseInt(stockMatch[1], 10);

      if (availableStock === 0) {
        return {
          message: 'Out of stock',
          shouldShowToast: true,
        };
      } else if (availableStock === 1) {
        return {
          message: 'Only 1 left in stock',
          shouldShowToast: true,
        };
      } else {
        return {
          message: `Only ${availableStock} left in stock`,
          shouldShowToast: true,
        };
      }
    } else {
      return {
        message: 'Limited stock available',
        shouldShowToast: true,
      };
    }
  }

  // Generic error
  return {
    message: errorMessage,
    shouldShowToast: true,
  };
}

/**
 * Check if a caught error has already been handled and shown to the user
 * Used in catch blocks to prevent duplicate toast notifications
 *
 * @param error - The caught error object
 * @returns true if error was already handled, false otherwise
 */
export function isErrorAlreadyHandled(error: any): boolean {
  if (!error?.message) return false;

  const handledErrorPatterns = [
    'out of stock',
    'left in stock',
    'limited stock',
    'concentrate limit',
    'grams of concentrated product',
    '8g concentrate',
    'Failed to update cart',
  ];

  return handledErrorPatterns.some((pattern) =>
    error.message.toLowerCase().includes(pattern.toLowerCase()),
  );
}
